import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "./Header";
import Footer from "../landing/Footer";
import LoadingSkeleton from "./LoadingSkeleton";
import blogApi from "../callapi/BlogApiNew";
import "./blog.css";

const MainBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Fetch initial posts
  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Add small delay to allow cache to be populated
      await new Promise((resolve) => setTimeout(resolve, 100));

      const result = await blogApi.getPosts(1, 6);

      setPosts(result.posts);
      setTotalPages(result.totalPages);
      setCurrentPage(1);
      setHasMore(result.totalPages > 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load more posts
  const loadMorePosts = async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      setError(null);

      const nextPage = currentPage + 1;
      const result = await blogApi.getPosts(nextPage, 6);

      setPosts((prevPosts) => [...prevPosts, ...result.posts]);
      setCurrentPage(nextPage);
      setHasMore(nextPage < result.totalPages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  // Format date
  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "Asia/Ho_Chi_Minh",
    };
    return new Date(dateString).toLocaleDateString("vi-VN", options);
  };

  // Extract excerpt from content
  const getExcerpt = (content, maxLength = 150) => {
    const text = content.replace(/<[^>]*>/g, "");
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  // Get featured image
  const getFeaturedImage = (post) => {
    if (
      post._embedded &&
      post._embedded["wp:featuredmedia"] &&
      post._embedded["wp:featuredmedia"][0]
    ) {
      return post._embedded["wp:featuredmedia"][0].source_url;
    }
    return "/src/assets/images/blog/blog1.png"; // Default image
  };

  if (loading) {
    return (
      <>
        <Header />
        <main className="blog-main">
          <div className="container">
            <div className="blog-header">
              <h1>Blog Pay2S</h1>
            </div>
            <LoadingSkeleton type="posts" count={6} />
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <div className="blog-container">
        <Header />
        <main className="blog-main">
          <div className="container">
            <div className="error-message">
              <h2>Có lỗi xảy ra</h2>
              <p>{error}</p>
              <button onClick={() => fetchPosts()} className="retry-btn">
                Thử lại
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Blog Pay2S - Tin tức và kiến thức thanh toán điện tử</title>
        <meta
          name="description"
          content="Cập nhật tin tức mới nhất về thanh toán điện tử, fintech và các giải pháp ngân hàng số từ Pay2S"
        />
        <meta
          name="keywords"
          content="pay2s, blog, thanh toán điện tử, fintech, ngân hàng số, payment gateway"
        />
        <meta
          property="og:title"
          content="Blog Pay2S - Tin tức và kiến thức thanh toán điện tử"
        />
        <meta
          property="og:description"
          content="Cập nhật tin tức mới nhất về thanh toán điện tử, fintech và các giải pháp ngân hàng số từ Pay2S"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pay2s.vn/blog" />
        <link rel="canonical" href="https://pay2s.vn/blog" />
      </Helmet>

      <Header />

      <main className="blog-main">
        <div className="container">
          <div className="blog-header">
            <h1>Blog Pay2S</h1>
          </div>

          <div className="blog-grid">
            {posts.map((post) => (
              <article key={post.id} className="blog-card">
                <div className="blog-card-image">
                  <img
                    src={getFeaturedImage(post)}
                    alt={post.title.rendered}
                    onError={(e) => {
                      e.target.src = "/src/assets/images/blog/blog1.png";
                    }}
                  />
                </div>

                <div className="blog-card-content">
                  <div className="blog-meta">
                    <span className="blog-date">{formatDate(post.date)}</span>
                    {post._embedded &&
                      post._embedded["wp:term"] &&
                      post._embedded["wp:term"][0] && (
                        <span className="blog-category">
                          {post._embedded["wp:term"][0][0]?.name}
                        </span>
                      )}
                  </div>

                  <h2 className="blog-title">
                    <Link
                      to={`/blog/${post.slug}`}
                      dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                    />
                  </h2>

                  <div className="blog-excerpt">
                    {getExcerpt(post.excerpt.rendered)}
                  </div>

                  <Link to={`/blog/${post.slug}`} className="read-more-btn">
                    Đọc thêm →
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="load-more-section">
              <button
                onClick={loadMorePosts}
                disabled={loadingMore}
                className="load-more-btn"
              >
                {loadingMore ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-2"></i>
                    Đang tải...
                  </>
                ) : (
                  <>
                    Xem thêm bài viết
                    <i className="fas fa-chevron-down ms-2"></i>
                  </>
                )}
              </button>

              {loadingMore && (
                <div className="loading-more-posts">
                  <LoadingSkeleton type="posts" count={6} />
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
};

export default MainBlog;
